/**
 * GherkinTab.tsx
 * Dedicated tab for Gherkin (.feature file) generation.
 * Shows generation runs for the selected test-cases artifact in a Runs panel (left),
 * and the feature file preview with toggle in the right panel.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { ArrowClockwise, Clipboard, Download, FileEarmarkCode, Paperclip } from 'react-bootstrap-icons';

import {
    fetchGherkinRuns,
    fetchGherkinStatus,
    generateGherkin,
    downloadGherkinArtifact,
    GherkinRunEntry,
    GherkinContextFileEntry,
    GherkinStatusResponse,
} from '../../api/requirements';
import { useProjects } from '../../contexts/ProjectContext';
import { getTestArtifact } from './RequirementTestWorkspace';
import GherkinContextModal from './GherkinContextModal';
import { formatApiError } from '../../utils/apiError';
import { projectArtifactDownloadFilename, triggerDownloadFromBlob } from '../../utils/download';
import './RequirementTestWorkspace.css';

const POLL_INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Gherkin parser — groups Scenarios by Feature and Rule for the "By Test Case"
// view without any extra dependencies.
// ---------------------------------------------------------------------------

interface GherkinScenario {
    tags: string[];
    title: string;
    steps: string[];
}

interface GherkinRule {
    title: string;
    scenarios: GherkinScenario[];
}

interface GherkinFeature {
    title: string;
    rules: GherkinRule[];
    /** Scenarios that appear directly under the Feature (no Rule block) */
    loose: GherkinScenario[];
}

function parseFeatureFile(text: string): GherkinFeature[] {
    const lines = text.split('\n');
    const features: GherkinFeature[] = [];
    let currentFeature: GherkinFeature | null = null;
    let currentRule: GherkinRule | null = null;
    let currentScenario: GherkinScenario | null = null;
    let pendingTags: string[] = [];

    const commitScenario = () => {
        if (!currentScenario || !currentFeature) return;
        if (currentRule) {
            currentRule.scenarios.push(currentScenario);
        } else {
            currentFeature.loose.push(currentScenario);
        }
        currentScenario = null;
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();

        if (line.startsWith('@')) {
            pendingTags.push(...line.split(/\s+/).filter((t) => t.startsWith('@')));
            continue;
        }

        if (line.startsWith('Feature:')) {
            commitScenario();
            currentRule = null;
            currentFeature = { title: line.replace('Feature:', '').trim(), rules: [], loose: [] };
            features.push(currentFeature);
            pendingTags = [];
            continue;
        }

        if (line.startsWith('Rule:')) {
            commitScenario();
            currentRule = { title: line.replace('Rule:', '').trim(), scenarios: [] };
            currentFeature?.rules.push(currentRule);
            pendingTags = [];
            continue;
        }

        if (line.startsWith('Scenario:') || line.startsWith('Scenario Outline:')) {
            commitScenario();
            const rawTitle = line.replace(/^Scenario( Outline)?:/, '').trim();
            // LLM sometimes appends tags directly to the title: "Title@tag1@tag2"
            const inlineTagMatch = rawTitle.match(/(@[\w-]+)+$/);
            const inlineTags = inlineTagMatch ? (inlineTagMatch[0].match(/@[\w-]+/g) ?? []) : [];
            const cleanTitle = rawTitle.replace(/(@[\w-]+)+$/, '').trim();
            currentScenario = { tags: [...pendingTags, ...inlineTags], title: cleanTitle, steps: [] };
            pendingTags = [];
            continue;
        }

        if (currentScenario && (
            line.startsWith('Given') ||
            line.startsWith('When') ||
            line.startsWith('Then') ||
            line.startsWith('And') ||
            line.startsWith('But') ||
            line.startsWith('*')
        )) {
            currentScenario.steps.push(line);
        }
    }
    commitScenario();
    return features;
}

// ---------------------------------------------------------------------------
// GherkinByTestView — "By Test Case" view component
// ---------------------------------------------------------------------------

function GherkinByTestView({ featureText }: { featureText: string }): React.ReactElement {
    const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
    const features = React.useMemo(() => parseFeatureFile(featureText), [featureText]);

    const toggle = (key: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const renderScenarios = (scenarios: GherkinScenario[], featureIdx: number, ruleIdx: number) =>
        scenarios.map((sc, scIdx) => {
            const key = `${featureIdx}-${ruleIdx}-${scIdx}`;
            const isOpen = expanded.has(key);
            return (
                <div key={key} className="rrw-gherkin-bytest-scenario">
                    <button
                        type="button"
                        className="rrw-gherkin-bytest-scenario-header"
                        onClick={() => toggle(key)}
                        aria-expanded={isOpen}
                    >
                        <span className="rrw-gherkin-bytest-chevron">{isOpen ? '▾' : '▸'}</span>
                        <span className="rrw-gherkin-bytest-scenario-title">{sc.title}</span>
                        {sc.tags.map((tag) => (
                            <span key={tag} className="rrw-gherkin-bytest-tag">{tag}</span>
                        ))}
                    </button>
                    {isOpen && (
                        <div className="rrw-gherkin-bytest-steps">
                            {sc.steps.map((step, si) => (
                                <div key={si} className="rrw-gherkin-bytest-step">{step}</div>
                            ))}
                        </div>
                    )}
                </div>
            );
        });

    if (features.length === 0) {
        return <div className="rrw-gherkin-preview-loading">No scenarios found in the feature file.</div>;
    }

    return (
        <div className="rrw-gherkin-bytest">
            {features.map((feat, fi) => (
                <div key={fi} className="rrw-gherkin-bytest-feature">
                    <div className="rrw-gherkin-bytest-feature-title">
                        <span className="rrw-gherkin-bytest-kw">Feature:</span> {feat.title}
                    </div>
                    {feat.loose.length > 0 && (
                        <div className="rrw-gherkin-bytest-rule">
                            {renderScenarios(feat.loose, fi, -1)}
                        </div>
                    )}
                    {feat.rules.map((rule, ri) => (
                        <div key={ri} className="rrw-gherkin-bytest-rule">
                            <div className="rrw-gherkin-bytest-rule-title">
                                <span className="rrw-gherkin-bytest-kw">Rule:</span> {rule.title}
                            </div>
                            {renderScenarios(rule.scenarios, fi, ri)}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

interface GherkinTabProps {
    projectId: number;
    selectedJobId?: number | null;
    startedJobId?: number | null;
    trackStatus?: boolean;
    isActive?: boolean;
}

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------
function StatusBadge({ status }: { status: string }): React.ReactElement {
    return <span className={`rrw-gherkin-badge rrw-gherkin-badge--${status}`}>{status}</span>;
}

function isActiveGherkinStatus(status?: string | null): boolean {
    return status === 'pending' || status === 'running';
}

function statusToRun(status: GherkinStatusResponse): GherkinRunEntry | null {
    if (!status.gherkin_job_id || status.status === 'not_started') return null;
    return {
        gherkin_job_id: status.gherkin_job_id,
        status: status.status,
        progress: status.progress ?? 0,
        error_message: status.error_message,
        created_at: status.created_at ?? new Date().toISOString(),
        completed_at: status.completed_at,
        gherkin_artifact_id: status.gherkin_artifact_id,
        context_files: status.context_files ?? [],
    };
}

// ---------------------------------------------------------------------------
// GherkinTab — main component
// ---------------------------------------------------------------------------
export default function GherkinTab({
    projectId,
    selectedJobId,
    startedJobId = null,
    trackStatus = false,
    isActive = true,
}: GherkinTabProps): React.ReactElement {
    const { projects, requirementJobs, fetchRequirementJobs } = useProjects();
    const projectName = projects.find((project) => project.id === projectId)?.name || `Project ${projectId}`;
    const gherkinDownloadName = projectArtifactDownloadFilename(
        projectName,
        'gherkin',
        'test_cases.feature',
    );

    const jobs = useMemo(
        () => requirementJobs[projectId] ?? [],
        [projectId, requirementJobs],
    );
    const testcaseJobs = useMemo(
        () => jobs.filter((job) => getTestArtifact(job)),
        [jobs],
    );
    const selectedJob = testcaseJobs.find((j) => j.id === selectedJobId)
        ?? testcaseJobs.find((j) => j.status === 'completed')
        ?? testcaseJobs[0]
        ?? null;
    const artifact = selectedJob ? getTestArtifact(selectedJob) : null;

    // Track whether we've attempted a job fetch for this project
    const [jobsFetchAttempted, setJobsFetchAttempted] = useState(false);

    // Runs list
    const [runs, setRuns] = useState<GherkinRunEntry[]>([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [selectedRunId, setSelectedRunId] = useState<number | null>(startedJobId);

    // Preview
    const [viewMode, setViewMode] = useState<'feature' | 'by-test'>('feature');
    const [featureText, setFeatureText] = useState<string | null>(null);
    const [featureLoading, setFeatureLoading] = useState(false);
    const [featureError, setFeatureError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    // UI
    const [error, setError] = useState<string | null>(null);
    const [gherkinModalOpen, setGherkinModalOpen] = useState(false);
    const pollRef = useRef<number | null>(null);

    // Sync startedJobId → selectedRunId
    useEffect(() => {
        if (startedJobId !== null) setSelectedRunId(startedJobId);
    }, [startedJobId]);

    // Derived
    const selectedRun = useMemo(
        () => runs.find((r) => r.gherkin_job_id === selectedRunId) ?? (runs.length > 0 ? runs[0] : null),
        [runs, selectedRunId],
    );
    const activeRun = useMemo(
        () => runs.find((r) => r.status === 'pending' || r.status === 'running') ?? null,
        [runs],
    );
    const shouldPollGherkin = useMemo(
        () => Boolean(activeRun || (trackStatus && startedJobId !== null && selectedRun && isActiveGherkinStatus(selectedRun.status))),
        [activeRun, selectedRun, startedJobId, trackStatus],
    );
    const parsedFeatures = useMemo(() => (featureText ? parseFeatureFile(featureText) : []), [featureText]);
    const scenarioCount = useMemo(
        () => parsedFeatures.reduce((sum, f) => sum + f.loose.length + f.rules.reduce((rs, r) => rs + r.scenarios.length, 0), 0),
        [parsedFeatures],
    );

    const stopPoll = useCallback(() => {
        if (pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const upsertRun = useCallback((run: GherkinRunEntry) => {
        setRuns((prev) => {
            const existingIndex = prev.findIndex((item) => item.gherkin_job_id === run.gherkin_job_id);
            if (existingIndex === -1) {
                return [run, ...prev];
            }
            const next = [...prev];
            next[existingIndex] = { ...next[existingIndex], ...run };
            return next;
        });
    }, []);

    const refreshLatestStatus = useCallback(async (): Promise<GherkinRunEntry | null> => {
        if (!selectedJob || !artifact) return null;
        const status = await fetchGherkinStatus(selectedJob.project_id, selectedJob.id, artifact.id);
        const run = statusToRun(status);
        if (run) {
            upsertRun(run);
        }
        return run;
    }, [artifact, selectedJob, upsertRun]);

    const refreshRuns = useCallback(async (showLoader = false) => {
        if (!selectedJob || !artifact) return;
        if (showLoader) setRunsLoading(true);
        try {
            const data = await fetchGherkinRuns(selectedJob.project_id, selectedJob.id, artifact.id);
            setRuns(data);
            setError(null);
        } catch (err: any) {
            try {
                await refreshLatestStatus();
            } catch (statusErr: any) {
                setError(
                    statusErr?.response?.data?.detail
                    || err?.response?.data?.detail
                    || statusErr?.message
                    || err?.message
                    || 'Failed to refresh Gherkin generation status.',
                );
            }
        } finally {
            if (showLoader) setRunsLoading(false);
        }
    }, [artifact, refreshLatestStatus, selectedJob]);

    const loadFeaturePreview = useCallback(async (gherkinJobId: number, gherkinArtifactId: number) => {
        if (!selectedJob) return;
        setFeatureLoading(true);
        setFeatureError(null);
        try {
            const downloaded = await downloadGherkinArtifact(
                selectedJob.project_id,
                gherkinJobId,
                gherkinArtifactId,
                gherkinDownloadName,
            );
            const text = await downloaded.blob.text();
            setFeatureText(text);
        } catch (err: any) {
            setFeatureError(err?.response?.data?.detail || err?.message || 'Failed to load Gherkin preview.');
        } finally {
            setFeatureLoading(false);
        }
    }, [gherkinDownloadName, selectedJob]);

    const handleCopyFeature = useCallback(async () => {
        if (!featureText) return;
        try {
            await navigator.clipboard.writeText(featureText);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setError('Failed to copy Gherkin text.');
        }
    }, [featureText]);

    const handleDownload = useCallback(async () => {
        if (!selectedJob || !selectedRun?.gherkin_artifact_id) return;
        try {
            const downloaded = await downloadGherkinArtifact(
                selectedJob.project_id,
                selectedRun.gherkin_job_id,
                selectedRun.gherkin_artifact_id,
                gherkinDownloadName,
            );
            triggerDownloadFromBlob(downloaded.blob, downloaded.filename);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to download Gherkin file.');
        }
    }, [gherkinDownloadName, selectedJob, selectedRun]);

    const handleGenerate = useCallback(async (files: File[] = [], descriptions: string[] = []) => {
        if (!selectedJob || !artifact) return;
        setGherkinModalOpen(false);
        setError(null);
        setFeatureText(null);
        setFeatureError(null);

        // Add an optimistic placeholder immediately so the UI reacts at once
        const OPTIMISTIC_ID = -Date.now(); // negative so it never clashes with a real DB id
        const optimisticRun: GherkinRunEntry = {
            gherkin_job_id: OPTIMISTIC_ID,
            status: 'pending',
            progress: 0,
            error_message: null,
            created_at: new Date().toISOString(),
            completed_at: null,
            gherkin_artifact_id: null,
            context_files: files.map((f, i) => ({ filename: f.name, description: descriptions[i] ?? '' })),
        };
        setRuns((prev) => [optimisticRun, ...prev]);
        setSelectedRunId(OPTIMISTIC_ID);

        try {
            const response = await generateGherkin(
                selectedJob.project_id,
                selectedJob.id,
                artifact.id,
                files,
                descriptions,
            );
            // Replace the placeholder with the real run from the server
            setRuns((prev) =>
                prev.map((r) =>
                    r.gherkin_job_id === OPTIMISTIC_ID
                        ? {
                            ...r,
                            gherkin_job_id: response.gherkin_job_id,
                            status: response.status,
                            created_at: response.created_at,
                            context_files: response.context_files ?? r.context_files,
                          }
                        : r,
                ),
            );
            setSelectedRunId(response.gherkin_job_id);
            void refreshRuns();
        } catch (err: any) {
            // Remove the placeholder and surface the error
            setRuns((prev) => prev.filter((r) => r.gherkin_job_id !== OPTIMISTIC_ID));
            setSelectedRunId(null);
            setError(formatApiError(err, 'Failed to start Gherkin generation.'));
        }
    }, [artifact, refreshRuns, selectedJob]);

    useEffect(() => {
        if (!trackStatus || startedJobId === null) return;
        setSelectedRunId(startedJobId);
        upsertRun({
            gherkin_job_id: startedJobId,
            status: 'pending',
            progress: 0,
            error_message: null,
            created_at: new Date().toISOString(),
            completed_at: null,
            gherkin_artifact_id: null,
            context_files: [],
        });
    }, [startedJobId, trackStatus, upsertRun]);

    // Initial load when artifact changes
    useEffect(() => {
        stopPoll();
        if (!isActive) return;
        setRuns((prev) => (trackStatus && startedJobId !== null ? prev : []));
        setFeatureText(null);
        setFeatureError(null);
        if (selectedJob && artifact) {
            void refreshRuns(true);
        }
    }, [artifact, artifact?.id, isActive, refreshRuns, selectedJob, selectedJob?.id, startedJobId, stopPoll, trackStatus]);

    // Poll when there's an active run
    useEffect(() => {
        stopPoll();
        if (isActive && shouldPollGherkin) {
            void refreshRuns();
            pollRef.current = window.setInterval(() => void refreshRuns(), POLL_INTERVAL_MS);
        }
        return stopPoll;
    }, [isActive, refreshRuns, shouldPollGherkin, stopPoll]);

    // Auto-select best run when list changes
    useEffect(() => {
        if (runs.length === 0) return;
        const exists = selectedRunId !== null && runs.some((r) => r.gherkin_job_id === selectedRunId);
        if (!exists) {
            const best = runs.find((r) => r.status === 'completed') ?? runs[0];
            setSelectedRunId(best.gherkin_job_id);
        }
    }, [runs]); // eslint-disable-line

    // Load feature text when selected run changes
    useEffect(() => {
        if (!isActive) return;
        if (selectedRun?.status === 'completed' && selectedRun.gherkin_artifact_id) {
            void loadFeaturePreview(selectedRun.gherkin_job_id, selectedRun.gherkin_artifact_id);
        } else {
            setFeatureText(null);
            setFeatureError(null);
        }
    }, [selectedRun?.gherkin_job_id, selectedRun?.status, selectedRun?.gherkin_artifact_id]); // eslint-disable-line

    // Cleanup
    useEffect(() => () => stopPoll(), [stopPoll]);

    // If jobs haven't been loaded yet (e.g. direct page refresh on Gherkin tab), fetch them
    useEffect(() => {
        if (isActive && !jobsFetchAttempted && jobs.length === 0) {
            setJobsFetchAttempted(true);
            void fetchRequirementJobs(projectId).catch(() => undefined);
        }
    }, [fetchRequirementJobs, isActive, jobs.length, jobsFetchAttempted, projectId]);

    /* ── No artifact — show loading spinner until we've tried fetching ── */
    if (!artifact) {
        if (!jobsFetchAttempted && jobs.length === 0) {
            return (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--rrw-muted)' }}>
                    <Spinner className="mb-3" />
                    <p style={{ marginBottom: 0, fontSize: '0.875rem' }}>Loading…</p>
                </div>
            );
        }
        return (
            <div className="rrw-gherkin-empty">
                <FileEarmarkCode size={48} className="rrw-gherkin-empty-icon" />
                <p className="rrw-gherkin-empty-title">No test cases found</p>
                <p className="rrw-gherkin-empty-body">
                    Generate test cases first from the <strong>Requirements &amp; Code</strong> tab, then come back here to convert them to Gherkin format.
                </p>
            </div>
        );
    }

    const isGenerating = !!activeRun;
    const selectedRunCompleted = selectedRun?.status === 'completed';

    return (
        <>
        <div className="rrw-page rrw-page-embedded">

            {/* ── Hero ── */}
            <div className="rrw-hero rrw-hero-embedded">
                <div>
                    <p className="rrw-hero-kicker">BDD / Gherkin</p>
                    <h1 className="rrw-hero-title">Gherkin Feature File</h1>
                    <p className="rrw-hero-copy">
                        Transform your generated test cases into a structured <code>.feature</code> file using Gherkin BDD syntax. Each scenario is expressed in plain English with <code>Feature</code>, <code>Rule</code>, <code>Scenario</code>, and <code>Given / When / Then</code> steps, making them immediately usable in frameworks like Cucumber, Behave, and SpecFlow.
                    </p>
                </div>
                <div className="rrw-hero-actions">
                    {selectedRunCompleted ? (
                        <>
                            <Button className="rrw-hero-action-button" variant="light" size="sm"
                                onClick={() => setGherkinModalOpen(true)} disabled={isGenerating}>
                                <ArrowClockwise size={13} className="me-1" />Regenerate
                            </Button>
                            <Button className="rrw-hero-action-button rrw-hero-action-button-download" variant="light" size="sm"
                                onClick={() => void handleDownload()}>
                                <Download size={13} className="me-1" />Download .feature
                            </Button>
                        </>
                    ) : (
                        <Button className="rrw-hero-action-button" variant="light" size="sm"
                            onClick={() => setGherkinModalOpen(true)} disabled={isGenerating}>
                            {isGenerating
                                ? <><Spinner size="sm" className="me-1" />Generating…</>
                                : <><FileEarmarkCode size={13} className="me-1" />Generate Gherkin</>
                            }
                        </Button>
                    )}
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <Alert variant="danger" dismissible className="mt-3" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* ── Workspace ── */}
            <div className="rrw-workspace">

                {/* ── Left: Runs panel ── */}
                <div className="rrw-column">
                    <div className="rrw-panel">
                        <div className="rrw-panel-header">
                            <h3 className="rrw-panel-title">Runs</h3>
                            <span className="rrw-panel-subtitle">
                                {!runsLoading && `${runs.length} run${runs.length !== 1 ? 's' : ''}`}
                            </span>
                        </div>
                        <div className="rrw-job-list">
                            {runsLoading && runs.length === 0 ? (
                                <div style={{ padding: 16, textAlign: 'center', color: 'var(--rrw-muted)', fontSize: '0.875rem' }}>
                                    <Spinner size="sm" className="me-2" />Loading runs...
                                </div>
                            ) : runs.length === 0 ? (
                                <div style={{ padding: 24, textAlign: 'center', color: 'var(--rrw-muted)', fontSize: '0.875rem' }}>
                                    <FileEarmarkCode size={28} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.4 }} />
                                    No runs yet.
                                    <br />
                                    <button type="button" className="btn btn-sm btn-outline-primary mt-2"
                                        onClick={() => setGherkinModalOpen(true)}>
                                        Generate Gherkin
                                    </button>
                                </div>
                            ) : (
                                runs.map((run, idx) => {
                                    const isActive = run.gherkin_job_id === (selectedRun?.gherkin_job_id ?? null);
                                    const isRunning = run.status === 'pending' || run.status === 'running';
                                    return (
                                        <button
                                            key={run.gherkin_job_id}
                                            type="button"
                                            className={`rrw-job-item${isActive ? ' rrw-job-item-active' : ''}`}
                                            onClick={() => setSelectedRunId(run.gherkin_job_id)}
                                        >
                                            <div className="rrw-job-item-header">
                                                <span style={{ fontWeight: 600, fontSize: '0.84rem' }}>
                                                    Run #{runs.length - idx}
                                                </span>
                                                <StatusBadge status={run.status} />
                                            </div>
                                            <div className="rrw-job-item-meta">
                                                <span style={{ fontSize: '0.78rem', color: 'var(--rrw-muted)' }}>
                                                    {new Date(run.created_at).toLocaleDateString()}{' '}
                                                    {new Date(run.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            {run.context_files && run.context_files.length > 0 && (
                                                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {run.context_files.map((cf: GherkinContextFileEntry, fi: number) => (
                                                        <span key={fi} title={cf.description || cf.filename}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', background: '#f0f2f5', color: '#555', borderRadius: 4, padding: '1px 6px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            <Paperclip size={10} />{cf.filename}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {isRunning && (
                                                <div className="progress mt-1" style={{ height: 4 }}>
                                                    <div className="progress-bar progress-bar-striped progress-bar-animated"
                                                        style={{ width: `${run.progress ?? 30}%` }} />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Right: Feature preview ── */}
                <div className="rrw-column">
                    <div className="rrw-panel">
                        {/* Panel header with toggle */}
                        <div className="rrw-panel-header">
                            <div>
                                <h3 className="rrw-panel-title">Preview</h3>
                                {selectedRunCompleted && featureText && (
                                    <p className="rrw-panel-subtitle">{scenarioCount} scenario{scenarioCount !== 1 ? 's' : ''}</p>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <div className="btn-group btn-group-sm" role="group">
                                    <button type="button"
                                        className={`btn btn-outline-secondary${viewMode === 'feature' ? ' active' : ''}`}
                                        onClick={() => setViewMode('feature')}>
                                        Feature File
                                    </button>
                                    <button type="button"
                                        className={`btn btn-outline-secondary${viewMode === 'by-test' ? ' active' : ''}`}
                                        onClick={() => setViewMode('by-test')}>
                                        By Test Case
                                    </button>
                                </div>
                                {selectedRunCompleted && (
                                    <Button variant="outline-secondary" size="sm"
                                        onClick={() => void handleCopyFeature()} disabled={!featureText || featureLoading}>
                                        <Clipboard size={13} className="me-1" />
                                        {copied ? 'Copied' : 'Copy'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Panel content */}
                        {!selectedRun ? (
                            <div className="rrw-gherkin-info" style={{ margin: 16 }}>
                                <strong>What is Gherkin?</strong>
                                <p>
                                    Gherkin is a plain-English business-readable DSL for writing BDD scenarios. Each scenario describes a behaviour using <code>Given</code> (preconditions), <code>When</code> (actions), and <code>Then</code> (outcomes). The generated <code>.feature</code> file can be used directly with Cucumber, Behave, SpecFlow, and similar frameworks.
                                </p>
                            </div>
                        ) : selectedRun.status === 'pending' || selectedRun.status === 'running' ? (
                            <div className="p-4 text-center">
                                <Spinner className="mb-3" />
                                <p style={{ marginBottom: 4, color: 'var(--rrw-muted)' }}>Generating Gherkin feature file…</p>
                                <p style={{ marginBottom: 0, fontSize: '0.82rem', color: 'var(--rrw-muted)' }}>
                                    {selectedRun.progress ?? 0}% complete
                                </p>
                            </div>
                        ) : selectedRun.status === 'failed' ? (
                            <Alert variant="warning" className="m-3">
                                <strong>Generation failed.</strong>
                                {selectedRun.error_message ? ` ${selectedRun.error_message}` : ''}
                            </Alert>
                        ) : featureLoading ? (
                            <div className="p-3 text-center" style={{ color: 'var(--rrw-muted)', fontSize: '0.875rem' }}>
                                <Spinner size="sm" className="me-2" />Loading feature file…
                            </div>
                        ) : featureError ? (
                            <Alert variant="warning" className="m-3">{featureError}</Alert>
                        ) : featureText ? (
                            viewMode === 'feature' ? (
                                <pre className="rrw-gherkin-preview-code"><code>{featureText}</code></pre>
                            ) : (
                                <GherkinByTestView featureText={featureText} />
                            )
                        ) : (
                            <div className="p-3" style={{ color: 'var(--rrw-muted)', fontSize: '0.875rem' }}>
                                Feature preview not available.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>

        {/* ── Context file upload modal ── */}
        <GherkinContextModal
            show={gherkinModalOpen}
            onHide={() => setGherkinModalOpen(false)}
            onSubmit={(files, descs) => void handleGenerate(files, descs)}
            submitting={false}
        />
        </>
    );
}
